import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POOJA_LABELS = { 'thilahomam': 'Thilahomam', 'ganapathi-homam': 'Ganapathi Homam', 'archana': 'Archana' };

const STATUS_CONFIG = {
  pending: { icon: Clock, color: '#D97706', bg: '#FEF3C7', label: 'Pending' },
  confirmed: { icon: CheckCircle2, color: '#059669', bg: '#D1FAE5', label: 'Confirmed' },
  completed: { icon: CheckCircle2, color: '#059669', bg: '#D1FAE5', label: 'Completed' },
  cancelled: { icon: XCircle, color: '#DC2626', bg: '#FEE2E2', label: 'Cancelled' }
};

const PAYMENT_CONFIG = {
  pending: { color: '#D97706', bg: '#FEF3C7', label: 'Payment Pending' },
  verification_pending: { color: '#2563EB', bg: '#DBEAFE', label: 'Verification Pending' },
  verified: { color: '#059669', bg: '#D1FAE5', label: 'Payment Verified' },
  rejected: { color: '#DC2626', bg: '#FEE2E2', label: 'Payment Rejected' }
};

export default function BookingStatus() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/booking/${bookingId}`).then(res => setBooking(res.data)).catch(() => setError('Booking not found'));
  }, [bookingId]);

  if (error) return <div className="min-h-screen flex items-center justify-center"><p className="text-red-600">{error}</p></div>;
  if (!booking) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#C2410C' }}></div></div>;

  const sc = STATUS_CONFIG[booking.booking_status] || STATUS_CONFIG.pending;
  const pc = PAYMENT_CONFIG[booking.payment_status] || PAYMENT_CONFIG.pending;
  const StatusIcon = sc.icon;
  const isCart = booking.order_type === 'cart' && Array.isArray(booking.items);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="text-lg font-bold" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Booking Status</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-20">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-6" data-testid="booking-status-page">
          {/* Status */}
          <div className="text-center space-y-3">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: sc.bg }}>
              <StatusIcon className="h-8 w-8" style={{ color: sc.color }} />
            </div>
            <h2 className="text-2xl font-bold" style={{ color: '#1C1917' }}>{sc.label}</h2>
            <p className="text-sm px-3 py-1 rounded-full inline-block font-medium" style={{ backgroundColor: pc.bg, color: pc.color }}>{pc.label}</p>
          </div>

          {/* Details */}
          <div className="rounded-2xl p-5 space-y-3" style={{ backgroundColor: '#F5F5F4' }}>
            <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>Booking ID</span><span className="font-bold" style={{ color: '#1C1917' }}>{booking.booking_id}</span></div>

            {isCart ? (
              <>
                {booking.items.map((item, idx) => (
                  <div key={idx} className="rounded-lg p-3" style={{ backgroundColor: '#FAFAF9', border: '1px solid #E7E5E4' }}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold text-sm" style={{ color: '#1C1917' }}>{POOJA_LABELS[item.pooja_type] || item.pooja_type}</span>
                      <span className="font-bold text-sm" style={{ color: '#C2410C' }}>₹{item.subtotal}</span>
                    </div>
                    <div className="text-xs space-y-0.5" style={{ color: '#78716C' }}>
                      <div>Name: {item.customer_name || item.death_person_name || item.devotee_name}</div>
                      {item.nakshatra && <div>Nakshatra: {item.nakshatra}</div>}
                      {item.death_date && <div>Death Date: {item.death_date}</div>}
                      {item.birth_nakshatra && <div>Birth Nakshatra: {item.birth_nakshatra}</div>}
                    </div>
                  </div>
                ))}
                <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>WhatsApp</span><span className="font-semibold">{booking.whatsapp}</span></div>
              </>
            ) : (
              <>
                <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>Pooja</span><span className="font-semibold">{POOJA_LABELS[booking.pooja_type] || booking.pooja_type}</span></div>
                <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>Name</span><span className="font-semibold">{booking.customer_name}</span></div>
                <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>WhatsApp</span><span className="font-semibold">{booking.whatsapp}</span></div>
                {booking.death_date && <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>Death Date</span><span className="font-semibold">{booking.death_date}</span></div>}
                {booking.nakshatra && <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>Nakshatra</span><span className="font-semibold">{booking.nakshatra}</span></div>}
              </>
            )}

            {booking.utr_number && <div className="flex justify-between text-sm"><span style={{ color: '#78716C' }}>UTR Number</span><span className="font-semibold">{booking.utr_number}</span></div>}
            <div className="border-t pt-3 mt-3" style={{ borderColor: '#D6D3D1' }}>
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Total</span>
                <span className="text-2xl font-bold" style={{ color: '#C2410C' }}>₹{booking.total}</span>
              </div>
            </div>
          </div>

          {booking.booking_status === 'cancelled' && booking.cancellation_reason && (
            <div className="rounded-xl p-4 border" style={{ backgroundColor: '#FEF2F2', borderColor: '#FCA5A5' }}>
              <p className="text-sm font-medium" style={{ color: '#991B1B' }}>Cancellation Reason: {booking.cancellation_reason}</p>
            </div>
          )}

          <Button variant="outline" onClick={() => navigate('/')} className="w-full rounded-xl p-4" data-testid="back-home-btn">Back to Home</Button>
        </div>
      </div>
    </div>
  );
}
